import React from 'react';
import styled from 'styled-components';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
  line-height: 1.8;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 900;
  margin-bottom: 10px;
  color: var(--color-black);
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const LastUpdated = styled.p`
  font-size: 0.9rem;
  color: var(--color-gray);
  text-align: center;
  margin-bottom: 30px;
  font-style: italic;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 40px 0 20px 0;
  color: var(--color-black);
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const Paragraph = styled.p`
  font-size: 1rem;
  color: var(--color-black);
  margin-bottom: 20px;
  text-align: justify;
  
  @media (max-width: 768px) {
    text-align: left;
  }
`;

const List = styled.ul`
  font-size: 1rem;
  color: var(--color-black);
  margin-bottom: 20px;
  padding-left: 20px;
`;

const ListItem = styled.li`
  margin-bottom: 10px;
`;

const SubSectionTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  margin: 25px 0 15px 0;
  color: var(--color-black);
`;

const ContactInfo = styled.p`
  font-size: 1rem;
  color: var(--color-black);
  margin: 20px 0;
  font-weight: 600;
`;

const TermsScreen: React.FC = () => {
  return (
    <Container>
      <Title>Terms of Use for Accord</Title>
      <LastUpdated>Last Updated: October 15, 2025</LastUpdated>
      
      <Paragraph>
        Welcome to Accord! These Terms of Use ("Terms") govern your access to and use of the Accord mobile application and its related services (collectively, the "Service"). The Service is provided by the Accord Team ("we," "us," or "our").
      </Paragraph>
      
      <Paragraph>
        Please read these Terms carefully before using our Service.
      </Paragraph>
      
      <SectionTitle>1. Agreement to Terms</SectionTitle>
      
      <Paragraph>
        By creating an account, making a purchase, or using our Service in any way, you agree to be bound by these Terms and our Privacy Policy, which is incorporated by reference into these Terms. If you do not agree to these Terms, you may not access or use the Service.
      </Paragraph>
      
      <SectionTitle>2. The Service</SectionTitle>
      
      <Paragraph>
        Accord provides a Service that uses artificial intelligence to generate unique digital wallpapers ("Generated Content") based on images you upload ("User Content"). The Service may offer free previews and require payment for full-featured, high-resolution downloads.
      </Paragraph>
      
      <SectionTitle>3. User Accounts</SectionTitle>
      
      <SubSectionTitle>Account Creation:</SubSectionTitle>
      <Paragraph>
        To use most features of the Service, you must register for an account. You agree to provide accurate and complete information and to keep this information up to date.
      </Paragraph>
      
      <SubSectionTitle>Account Responsibility:</SubSectionTitle>
      <Paragraph>
        You are responsible for all activities that occur under your account and for safeguarding your account password. You agree to notify us immediately of any unauthorized use of your account.
      </Paragraph>
      
      <SectionTitle>4. Purchases and Payments</SectionTitle>
      
      <SubSectionTitle>Paid Features:</SubSectionTitle>
      <Paragraph>
        Certain features of the Service, such as generating and downloading high-resolution wallpapers, may require a payment ("Purchase").
      </Paragraph>
      
      <SubSectionTitle>Third-Party Payment Processors:</SubSectionTitle>
      <Paragraph>
        All Purchases are processed through a third-party application store, such as the Google Play Store. You must abide by their terms of service when making a Purchase. We are not responsible for the performance of these third-party processors.
      </Paragraph>
      
      <SubSectionTitle>No Refunds:</SubSectionTitle>
      <Paragraph>
        All Purchases are final and non-refundable, except as required by applicable law.
      </Paragraph>
      
      <SubSectionTitle>Pricing:</SubSectionTitle>
      <Paragraph>
        We reserve the right to change the pricing for our Service at any time.
      </Paragraph>
      
      <SectionTitle>5. Content Ownership and Licenses</SectionTitle>
      
      <Paragraph>
        This section clarifies who owns what.
      </Paragraph>
      
      <SubSectionTitle>Your User Content:</SubSectionTitle>
      <Paragraph>
        You retain full ownership of the photos you upload to the Service ("User Content"). We do not claim any ownership rights to your photos.
      </Paragraph>
      
      <SubSectionTitle>Our License to Your User Content:</SubSectionTitle>
      <Paragraph>
        By uploading a photo, you grant Accord a limited, non-exclusive, worldwide, royalty-free license to use, reproduce, modify, and process your User Content solely for the purpose of operating the Service and generating the wallpaper for you. This license terminates once your Generated Content is created and delivered to you, as detailed in our Privacy Policy.
      </Paragraph>
      
      <SubSectionTitle>Ownership of Generated Content:</SubSectionTitle>
      <Paragraph>
        The AI-generated wallpapers ("Generated Content") are created by the Service. Subject to your full payment for the applicable Purchase, we grant you a limited, non-exclusive, non-transferable, non-sublicensable, worldwide license to use the Generated Content for your own personal, non-commercial purposes.
      </Paragraph>
      
      <SubSectionTitle>Restrictions on Generated Content:</SubSectionTitle>
      <Paragraph>
        This license does not permit you to: (a) sell, license, rent, or otherwise commercially exploit the Generated Content; (b) use the Generated Content in a way that infringes upon the rights of any third party; or (c) use the Generated Content as a trademark, logo, or other indication of origin.
      </Paragraph>
      
      <SectionTitle>6. Our Intellectual Property</SectionTitle>
      
      <Paragraph>
        Excluding your User Content and your licensed Generated Content, the Service itself, including all software, text, graphics, logos, and user interface elements, is the exclusive property of the Accord Team and its licensors and is protected by copyright, trademark, and other laws.
      </Paragraph>
      
      <SectionTitle>7. User Conduct and Responsibilities</SectionTitle>
      
      <Paragraph>
        You agree not to do any of the following:
      </Paragraph>
      
      <List>
        <ListItem>Upload any User Content that is illegal, harmful, threatening, defamatory, obscene, or infringes on any third party's rights, including copyright, trademark, privacy, or other personal or proprietary rights.</ListItem>
        <ListItem>Upload any User Content that you do not have the right to transmit.</ListItem>
        <ListItem>Attempt to reverse-engineer, decompile, or otherwise discover the source code of the Service.</ListItem>
        <ListItem>Use the Service for any commercial purpose not expressly permitted by these Terms.</ListItem>
        <ListItem>Interfere with or disrupt the integrity or performance of the Service.</ListItem>
      </List>
      
      <Paragraph>
        We reserve the right to terminate your account if you violate these rules.
      </Paragraph>
      
      <SectionTitle>8. Termination</SectionTitle>
      
      <Paragraph>
        You can stop using our Service and delete your account at any time by contacting us. We may also terminate or suspend your access to the Service at any time, without prior notice or liability, for any reason, including if you breach these Terms.
      </Paragraph>
      
      <SectionTitle>9. Disclaimers</SectionTitle>
      
      <Paragraph>
        THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS, WITHOUT WARRANTY OF ANY KIND. WE EXPRESSLY DISCLAIM ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING, BUT NOT LIMITED TO, WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE MAKE NO WARRANTY THAT THE SERVICE WILL MEET YOUR REQUIREMENTS OR THAT THE GENERATED CONTENT WILL BE ACCURATE, RELIABLE, OR OF A CERTAIN QUALITY.
      </Paragraph>
      
      <SectionTitle>10. Limitation of Liability</SectionTitle>
      
      <Paragraph>
        TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL THE ACCORD TEAM, ITS DIRECTORS, EMPLOYEES, OR PARTNERS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES (INCLUDING, WITHOUT LIMITATION, LOSS OF PROFITS, DATA, OR GOODWILL) ARISING OUT OF YOUR ACCESS TO OR USE OF, OR INABILITY TO ACCESS OR USE, THE SERVICE.
      </Paragraph>
      
      <Paragraph>
        IN NO EVENT SHALL OUR AGGREGATE LIABILITY FOR ALL CLAIMS RELATING TO THE SERVICE EXCEED THE TOTAL AMOUNT PAID BY YOU TO US IN THE 12 MONTHS PRECEDING THE CLAIM.
      </Paragraph>
      
      <SectionTitle>11. Governing Law</SectionTitle>
      
      <Paragraph>
        These Terms shall be governed and construed in accordance with the laws of the State of New South Wales, Australia, without regard to its conflict of law provisions.
      </Paragraph>
      
      <SectionTitle>12. Changes to Terms</SectionTitle>
      
      <Paragraph>
        We reserve the right to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
      </Paragraph>
      
      <SectionTitle>13. Contact Us</SectionTitle>
      
      <ContactInfo>
        If you have any questions about these Terms, please contact us at: hello@accordwp.com
      </ContactInfo>
    </Container>
  );
};

export default TermsScreen;
